// Maps a Supabase auth signUp error to a user-facing message.
//
// Extracted from AuthContext so the branch classification is unit-testable.
// The bug that prompted this: a rate-limit error ("email rate limit exceeded",
// code over_email_send_rate_limit) was shown as "Invalid email address" because
// the old catch-all matched any message containing the word "email".

export interface SignupErrorInfo {
  message: string;
  isDuplicateUser: boolean;
}

export interface RawSignupError {
  message?: string | null;
  status?: number | string | null;
  code?: string | null;
}

export function mapSignupError(error: RawSignupError): SignupErrorInfo {
  const msg = (error.message ?? "").toLowerCase();
  // status wins over code for the numeric HTTP check (matches the original);
  // rawCode keeps the string code (e.g. over_email_send_rate_limit) intact.
  const code = String(error.status ?? error.code ?? "").toLowerCase();
  const rawCode = String(error.code ?? "").toLowerCase();
  const details = JSON.stringify(error).toLowerCase();

  // Duplicate account — Supabase returns this in several shapes.
  if (
    msg.includes("user already registered") ||
    msg.includes("email already registered") ||
    msg.includes("already exists") ||
    msg.includes("already been registered") ||
    msg.includes("user already exists") ||
    msg.includes("email address is already") ||
    msg.includes("an account with this email") ||
    code === "email_already_exists" ||
    code === "422" ||
    code === "user_already_exists" ||
    details.includes("already registered") ||
    details.includes("already exists")
  ) {
    return {
      message:
        "An account with this email already exists. Please sign in instead.",
      isDuplicateUser: true,
    };
  }

  if (msg.includes("password")) {
    return {
      message:
        "Password does not meet requirements. Please use a stronger password.",
      isDuplicateUser: false,
    };
  }

  // Rate limit — checked BEFORE the email branch, because the message
  // ("email rate limit exceeded") contains "email" and would otherwise be
  // misread as an invalid address.
  if (
    rawCode === "over_email_send_rate_limit" ||
    rawCode === "over_request_rate_limit" ||
    code === "429" ||
    msg.includes("rate limit")
  ) {
    return {
      message:
        "Too many attempts right now. Please wait a minute and try again.",
      isDuplicateUser: false,
    };
  }

  // Genuinely invalid email — only actual invalid-address signals, not any
  // message that merely mentions "email".
  if (
    rawCode === "email_address_invalid" ||
    (msg.includes("email") &&
      (msg.includes("invalid") || msg.includes("valid")))
  ) {
    return {
      message: "Invalid email address. Please check and try again.",
      isDuplicateUser: false,
    };
  }

  // Unknown — surface the raw message rather than a wrong guess.
  return {
    message: error.message || "Something went wrong. Please try again.",
    isDuplicateUser: false,
  };
}

/**
 * GoTrue's duplicate signal on a *successful* signUp: with email confirmation
 * on, an already-registered email gets an obfuscated user whose `identities`
 * array is empty (no error, so mapSignupError never sees it). Only an empty
 * array counts — a missing `identities` is not proof of anything.
 */
export function isObfuscatedDuplicateSignup(
  user: { identities?: unknown[] | null } | null | undefined
): boolean {
  return Array.isArray(user?.identities) && user.identities.length === 0;
}
